import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { secureGet, deleteWithBody, securePost, api } from '../../config/api';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";
import { useLocation } from 'react-router-dom';

import { DoubleButton } from '../../components/PrimaryButton';
import { formatDate } from '../../utils/format';
import LoadingLogo from '../../components/LoadingLogo';
import Filter from '../../components/Filter';
import PageScroller from '../../components/PageScroller';
import DeletePopup from '../../components/DeletePopup';
import Rows from '../../components/Rows';
import SnackbarNotification from '../../components/SnackbarNotification';

import deleteIcon from '../../icons/trashR.svg';
import AddIcon from '../../icons/plusB.svg';

const Purchases = () => {
  const [, setPurchasesData] = useState([]);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    providerId: '',
    productId: '',
    dateRange: {},
    needle: '',
  });
  const [providers, setProviders] = useState([]);
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [isProductDeletePopupVisible, setProductDeletePopupVisible] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const history = useHistory();
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.showSnackbar) {
      showSnackbar(location.state.snackbarMessage, location.state.snackbarSeverity);
      history.replace({ ...location, state: undefined });
    }
  }, [location, history]);

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const columns = [
    {
      label: 'Date', key: 'purchaseDate', render: (date) => formatDate(date)
    },
    { label: t(`columns.purchaseNumber`), key: 'purchaseNumber' },
    { label: t(`columns.provider`), key: 'provider', render: (provider) => provider?.name },
    { label: 'Stock', key: 'repo', render: (repo) => repo?.name },
    { label: t(`columns.articleCount`), key: 'itemCount', render: (_, row) => row.products?.length || 0 },
    { label: t(`columns.totalQuantity`), key: 'totalQuantity', render: (_, row) => row.products?.reduce((sum, product) => sum + (product.quantity || 0), 0) || 0 },
    {
      label: ' ',
      key: 'actions',
      render: (_, row) => (
        <button
          onClick={() => handleDeleteClick(row)}
          className="w-8 h-8 flex items-center justify-center bg-white rounded-[12px] shadow-[0px_6px_26px_#626B7D12]"

        >
          <img src={deleteIcon} alt="Delete" width={16} height={16} />
        </button>
      ),
    },
  ];

  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
    { name: 'dateRange', type: 'daterange' },
    {
      name: 'providerId',
      label: t(`filters.purchases.provider`),
      type: 'dropdown',
      options: providers,
    },
    {
      name: 'productId',
      label: t(`filters.purchases.product`),
      type: 'dropdown',
      options: products,
    },
  ];

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const providerResponse = await secureGet('/providers', showSnackbar, { signal });
        const providerOptions = providerResponse.data.map((provider) => ({
          value: provider.id,
          label: provider.name,
        }));
        setProviders(providerOptions);

        const productsResponse = await secureGet("/products/unpaginated", showSnackbar, { signal });
        const productsOptions = productsResponse.data.map((product) => ({
          value: product.id,
          reference: product.reference,
          name: product.name,
        }));
        setProducts(productsOptions);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching dropdown options:', error);
        }
      }
    };

    fetchDropdownOptions();
    return () => {
      abortController.abort();
    };
  }, [abortController, signal]);

  const fetchPaginatedPurchases = useCallback(async (page = 0, size = pageSize, activeFilters = filters) => {
    setLoading(true);
    try {
      const requestBody = {
        providerId: activeFilters.providerId || null,
        productId: activeFilters.productId || null,
        startDate: activeFilters.dateRange?.startDate || null,
        endDate: activeFilters.dateRange?.endDate || null,
        needle: activeFilters.needle || null,
      };

      const response = await securePost(`/purchases?page=${page}&size=${size}`, requestBody, showSnackbar, { signal });

      const { content, totalPages } = response.data;
      setPurchasesData(content);
      setCurrentPageData(content);
      setTotalPages(totalPages);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error fetching purchases:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize, signal]);

  useEffect(() => {
    fetchPaginatedPurchases(0);
  }, [fetchPaginatedPurchases]);


  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
    fetchPaginatedPurchases(0, newPageSize, filters);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchPaginatedPurchases(page, pageSize, filters);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await deleteWithBody(`/purchases/delete`, { purchaseId: itemToDelete.id }, showSnackbar);
      setPurchasesData((prev) => prev.filter((item) => item.purchaseNumber !== itemToDelete.purchaseNumber));
      setCurrentPageData((prev) => prev.filter((item) => item.purchaseNumber !== itemToDelete.purchaseNumber));
      setDeletePopupVisible(false);
      setItemToDelete(null);
      showSnackbar(t(`confirmation.purchaseDelete`), 'success');
    } catch (error) {
      console.error('Error deleting purchase:', error);
    } finally {
      setDeleting(false);
      setDeletePopupVisible(false);
      setItemToDelete(null);
    }
  };

  const resetFilters = () => {
    setFilters({ providerId: '', productId: '', dateRange: {}, needle: '' });
    fetchPaginatedPurchases(0);
  };

  const handleFilterChange = (filterName, filterValue) => {
    const updatedFilters = { ...filters };

    if (filterName === "dateRange") {
      updatedFilters.dateRange = {
        startDate: filterValue?.startDate || null,
        endDate: filterValue?.endDate || null,
      };
    } else {
      updatedFilters[filterName] = filterValue;
    }

    setFilters(updatedFilters);
    setCurrentPage(0);
    fetchPaginatedPurchases(0, pageSize, updatedFilters);
  };

  const handleProductDelete = (purchase, product) => {
    setItemToDelete(purchase);
    setProductToDelete(product);
    setProductDeletePopupVisible(true);
  };

  const handleProductDeleteConfirm = async () => {
    if (!itemToDelete || !productToDelete) return;
    setDeletingProduct(true);
    try {
      await deleteWithBody(`/purchases/delete`, {
        purchaseId: itemToDelete.id,
        productId: productToDelete.product?.id || productToDelete.id,
      });

      await fetchPaginatedPurchases(currentPage, pageSize, filters);

      setProductDeletePopupVisible(false);
      showSnackbar(t(`confirmation.purchaseDelete`), 'success');
    } catch (error) {
      console.error("Error deleting product:", error);
      showSnackbar(t(`failure.purchaseDelete`), 'error');
    } finally {
      setDeletingProduct(false);
      setProductDeletePopupVisible(false);
      setItemToDelete(null);
      setProductToDelete(null);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {

      const fileUploadResponse = await api.post(
        '/products/import',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: localStorage.getItem('token'),
          },
        }
      );
      fetchPaginatedPurchases();
      showSnackbar(t(`confirmation.upload`), 'success');

    } catch (error) {
      console.error("File upload failed:", error);
      showSnackbar(t('errors.generic'), 'error');
    }
  };

  return (
    <div>
      <Helmet>
        <title>GrayFox - {t("titles.purchases")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t(`titles.purchases`)}</h1>
        <div className="ml-auto text-[16px] ">
          <DoubleButton
            manual={t(`primaryButtons.add_purchase`)}
            upload={t(`primaryButtons.upload_purchase`)}
            onManualClick={() => history.push("/purchases/add")}
            onUploadClick={handleUpload}
          />
        </div>
      </div>

      <Filter filters={filterOptions} onFilterChange={handleFilterChange} resetFilters={resetFilters} filterValues={filters} />

      {loading ? (
        <LoadingLogo />
      ) : (
        <>
          <Rows columns={columns} data={currentPageData} expandable={true} onDelete={handleProductDelete} />
          <PageScroller
            pageNumber={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}

      {isDeletePopupVisible && (
        <DeletePopup open={isDeletePopupVisible} onCancel={() => setDeletePopupVisible(false)} onConfirm={handleDeleteConfirm} loading={deleting} />
      )}

      {isProductDeletePopupVisible && (
        <DeletePopup
          open={isProductDeletePopupVisible}
          onCancel={() => setProductDeletePopupVisible(false)}
          onConfirm={handleProductDeleteConfirm}
          loading={deletingProduct}
        />
      )}

      {/* Reusable Snackbar */}
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Purchases;
